import { _isEmpty, _isNotBlank, _notNil } from '@/littledash';

const alphaNumericCharacters: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

type PluralForms = { one: string } & Partial<Record<Intl.LDMLPluralRule, string>>;
type PluralHole = string | number | PluralForms;
type PluralTag = (strings: TemplateStringsArray, ...holes: Array<PluralHole>) => string;

const PLURAL_RULES = new Map<string, Intl.PluralRules>();

/** One `Intl.PluralRules` per locale, built once: pluralize runs for every count a list renders. */
const pluralRulesOf = (locale: string): Intl.PluralRules => {
  const cached = PLURAL_RULES.get(locale);
  if (cached !== undefined) {
    return cached;
  }
  const rules = new Intl.PluralRules(locale);
  PLURAL_RULES.set(locale, rules);
  return rules;
};

export class StringUtils {
  static randomHexColor = (): string => {
    return '#000000'.replace(/0/g, function () {
      return (~~(Math.random() * 16)).toString(16);
    });
  };
  static randomString(length = 5): string {
    let result = '';

    for (let i = 0; i < length; i++) {
      result += alphaNumericCharacters.charAt(Math.floor(Math.random() * alphaNumericCharacters.length));
    }

    return result;
  }

  /**
   * Locale-independent lexicographic comparator for `Array#sort`. Unlike `String#localeCompare`, the
   * result does not depend on runtime locale settings, so sort order is stable across environments —
   * use it wherever deterministic ordering matters (e.g. graph layout).
   */
  static compareLexicographic = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

  static pluralize(count: number, word: string, plural?: string): string;
  static pluralize(count: number, options?: { locale?: string }): PluralTag;
  static pluralize(count: number, wordOrOptions?: string | { locale?: string }, plural?: string): string | PluralTag {
    const locale = typeof wordOrOptions === 'object' ? (wordOrOptions.locale ?? 'en') : 'en';
    const category = pluralRulesOf(locale).select(count);
    const pick = (hole: PluralHole): string =>
      typeof hole === 'object' ? (hole[category] ?? hole.other ?? `${hole.one}s`) : String(hole);

    if (typeof wordOrOptions === 'string') {
      return `${count} ${pick({ one: wordOrOptions, ...(_notNil(plural) ? { other: plural } : {}) })}`;
    }

    return (strings, ...holes) =>
      strings.reduce((sentence, text, index) => sentence + (index > 0 ? pick(holes[index - 1]) : '') + text, '');
  }
}

/**
 * Turns an identifier into readable words in sentence case: the fallback for any key without copy of its own.
 *
 * Handles snake_case (`load_animals` → "Load animals") and camelCase (`measurementAnalysis` →
 * "Measurement analysis"). Words are lowercased before the leading capital, which flattens an acronym
 * (`load_SEND_data` → "Load send data"): acceptable for a fallback, so anything that matters should have
 * copy of its own.
 */
export const prettifyKey = (key: string): string => {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[_\s]+/)
    .filter(_isNotBlank)
    .map((word) => word.toLowerCase());

  if (_isEmpty(words)) {
    return key;
  }

  const [first, ...rest] = words;
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ');
};
